import React from 'react';
import { Newspaper, ExternalLink } from 'lucide-react';

const mediaFeatures = [
  {
    id: 1,
    publication: 'CNBC TV18',
    logo: 'https://customer-assets.emergentagent.com/job_ea8b3b14-e3e6-436d-8ca4-049dd423dd0c/artifacts/08tq0wds_image.png',
    title: 'Gegadyne Energy developing next-gen fast-charging batteries',
    date: '2021',
    link: 'https://www.cnbctv18.com/',
  },
  {
    id: 2,
    publication: 'Business Standard',
    logo: 'https://bsmedia.business-standard.com/include/_mod/site/html5/images/business-standard-logo.png',
    title: 'Gegadyne Energy: Building batteries that charge in 15 minutes',
    date: '2020',
    link: 'https://www.business-standard.com/',
  },
  {
    id: 3,
    publication: 'Overdrive',
    logo: 'https://www.overdrive.in/wp-content/uploads/2023/12/od-logo-white-horizontal.png',
    title: 'Gegadyne Energy: Multi-Ion technology for industrial EVs',
    date: '2021',
    link: 'https://www.overdrive.in/',
  },
  {
    id: 4,
    publication: 'Financial Express',
    logo: 'https://customer-assets.emergentagent.com/job_power-solutions-72/artifacts/hbttj4k7_image.png',
    title: 'Gegadyne Energy: Fast charging battery tech for the future',
    date: '2021',
    link: 'https://www.financialexpress.com/life/technology-gegadyne-energy-fast-charging-battery-tech-for-the-future-2198415/',
  },
  {
    id: 5,
    publication: 'Economic Times',
    logo: 'https://customer-assets.emergentagent.com/job_power-solutions-72/artifacts/xqk1qoxs_image.png',
    title: "Charged up: Gegadyne's carbon-powered batteries can break China's monopoly on electric vehicles",
    date: '2019',
    link: 'https://economictimes.indiatimes.com/small-biz/startups/features/charged-up-gegadynes-carbon-powered-batteries-can-break-chinas-monopoly-on-electric-vehicles/articleshow/73132918.cms?from=mdr',
  },
  {
    id: 6,
    publication: 'Forbes India',
    logo: 'https://customer-assets.emergentagent.com/job_power-solutions-72/artifacts/51qwwy2d_image.png',
    title: 'Gegadyne Energy: Taking charge of the electric revolution',
    date: '2020',
    link: 'https://www.forbesindia.com/article/30-under-30-2020/gegadyne-energy-taking-charge-of-the-electric-revolution/57693/1',
  },
  {
    id: 7,
    publication: 'Mint',
    logo: 'https://customer-assets.emergentagent.com/job_power-solutions-72/artifacts/nemzfiky_image.png',
    title: 'This novel battery will charge electric vehicles within 15 minutes',
    date: '2018',
    link: 'https://www.livemint.com/Industry/pkggCdHX3REdBx2Sv4cLHK/This-novel-battery-will-charge-electric-vehicles-within-15-m.html',
  },
];

const MediaSection = () => {
  return (
    <section id="media" className="relative py-16 sm:py-20 lg:py-24 bg-gray-950 overflow-hidden" data-testid="media-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/30 via-gray-950 to-gray-950" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
            <Newspaper className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-xs sm:text-sm font-semibold uppercase tracking-wider">Press Coverage</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Gegadyne in the <span className="text-emerald-400">Media</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
            Featured in India's leading business and technology publications.
          </p>
        </div>

        {/* Publication Logos Row - Infinite Scroll Marquee */}
        <div className="relative mb-12 py-6 bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          {/* Gradient Masks */}
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-gray-950 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-gray-950 to-transparent z-10 pointer-events-none" />
          
          {/* Scrolling Container */}
          <div className="flex animate-marquee">
            {/* First set of logos */}
            {[...mediaFeatures, ...mediaFeatures].map((item, idx) => (
              <div key={`${item.id}-${idx}`} className="flex items-center justify-center mx-8 sm:mx-12 flex-shrink-0">
                <img 
                  src={item.logo} 
                  alt={item.publication}
                  className="h-8 sm:h-10 object-contain opacity-70 hover:opacity-100 transition-opacity"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span 
                  className="hidden text-white font-bold text-lg tracking-wide opacity-70"
                  style={{ display: 'none' }}
                >
                  {item.publication}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CSS for marquee animation */}
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 25s linear infinite;
          }
          .animate-marquee:hover {
            animation-play-state: paused;
          }
        `}</style>

        {/* Featured Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mediaFeatures.map((item, index) => (
            <a
              key={item.id}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`group bg-gray-800/30 hover:bg-gray-800/50 rounded-2xl border border-white/10 hover:border-emerald-500/30 p-6 transition-all duration-300 ${
                index === 0 ? 'md:col-span-2' : ''
              }`}
              data-testid={`media-item-${item.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <img 
                    src={item.logo} 
                    alt={item.publication}
                    className="h-6 sm:h-7 object-contain"
                  />
                  <span className="text-gray-500 text-sm">• {item.date}</span>
                </div>
                <ExternalLink className="w-5 h-5 text-gray-500 group-hover:text-emerald-400 transition-colors" />
              </div>
              <h3 className={`text-white font-semibold leading-tight group-hover:text-emerald-400 transition-colors ${
                index === 0 ? 'text-xl sm:text-2xl' : 'text-lg'
              }`}>
                "{item.title}"
              </h3>
              {index === 0 && (
                <p className="text-gray-400 mt-3 text-sm sm:text-base">
                  An in-depth feature on how Gegadyne Energy is developing next-generation battery technology for the future.
                </p>
              )}
            </a>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 sm:mt-12 text-center">
          <p className="text-gray-500 text-sm mb-4">For press inquiries</p>
          <a
            href="mailto:info@gegadyne.com"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
          >
            info@gegadyne.com
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default MediaSection;
